import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const packages = [
  {
    id: 'quick',
    title: 'Quick Shoot',
    duration: '30 minutes',
    photos: '20+ edited photos',
    price: 79,
    emoji: '📷',
    accent: '#A78BFA',
    popular: false,
  },
  {
    id: 'standard',
    title: 'Standard Session',
    duration: '1 hour',
    photos: '50+ edited photos',
    price: 149,
    emoji: '📸',
    accent: '#A78BFA',
    popular: true,
  },
  {
    id: 'premium',
    title: 'Premium Experience',
    duration: '2 hours',
    photos: '100+ photos & video highlights',
    price: 249,
    emoji: '🎬',
    accent: '#A78BFA',
    popular: false,
  },
];

const locations = [
  'Hotel Room',
  'Hotel Lobby',
  'Pool Area',
  'Garden',
  'Rooftop',
  'Restaurant',
  'Halls Gap Lookout',
  'Grampians National Park',
  'Boroka Lookout',
  'MacKenzie Falls',
  'Lake Bellfield',
  'Pinnacle Lookout',
];

function Photography() {
  const { hotelId, roomNumber } = useParams();
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!selectedPackage || !selectedLocation || !date || !time) return;
    setSubmitted(true);
  };

  const isReady = selectedPackage && selectedLocation && date && time;

  if (submitted) {
    const pkg = packages.find(p => p.id === selectedPackage);
    return (
      <div style={styles.page}>
        <div style={styles.orb1} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={styles.successPage}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            style={styles.successIconWrap}
          >
            <span style={styles.successIcon}>📸</span>
          </motion.div>
          <h1 style={styles.successTitle}>Booking Confirmed!</h1>
          <p style={styles.successSub}>Your photographer is reserved</p>

          <div style={styles.successCard}>
            <div style={styles.successRow}>
              <span style={styles.successLabel}>PACKAGE</span>
              <span style={styles.successValue}>{pkg.title}</span>
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={styles.successLabel}>LOCATION</span>
              <span style={styles.successValue}>{selectedLocation}</span>
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={styles.successLabel}>DATE & TIME</span>
              <span style={styles.successValue}>{date} at {time}</span>
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={styles.successLabel}>TOTAL</span>
              <span style={{ ...styles.successValue, color: '#A78BFA' }}>${pkg.price}</span>
            </div>
          </div>

          <p style={styles.successNote}>
            Our photographer will meet you at {selectedLocation}. Payment at time of service.
          </p>

          <motion.button
            whileTap={{ scale: 0.97 }}
            style={styles.backBtn}
            onClick={() => navigate(`/${hotelId}/${roomNumber}`)}
          >
            Back to Services
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.orb1} />
      <div style={styles.orb2} />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.header}
      >
        <motion.button
          whileTap={{ scale: 0.95 }}
          style={styles.backArrow}
          onClick={() => navigate(`/${hotelId}/${roomNumber}`)}
        >
          ← Back
        </motion.button>
        <div style={styles.headerCenter}>
          <span style={styles.headerEmoji}>📸</span>
          <h1 style={styles.headerTitle}>Photo & Video</h1>
          <p style={styles.headerSub}>Capture your perfect moment</p>
        </div>
        <div style={styles.roomBadge}>
          <span style={styles.roomDot} />
          <span style={styles.roomText}>Room {roomNumber}</span>
        </div>
      </motion.div>

      {/* Gold line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.6 }}
        style={styles.goldLine}
      />

      <div style={styles.content}>
        {/* Packages */}
        <p style={styles.sectionLabel}>CHOOSE YOUR PACKAGE</p>
        <div style={styles.packages}>
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedPackage(pkg.id)}
              style={{
                ...styles.packageCard,
                border: selectedPackage === pkg.id
                  ? '1px solid #A78BFA66'
                  : '1px solid #ffffff08',
                background: selectedPackage === pkg.id
                  ? '#A78BFA12'
                  : '#111',
                boxShadow: selectedPackage === pkg.id
                  ? '0 0 30px rgba(167,139,250,0.1)'
                  : 'none',
              }}
            >
              {pkg.popular && (
                <div style={styles.popularBadge}>MOST POPULAR</div>
              )}
              <div style={styles.packageTop}>
                <span style={styles.packageEmoji}>{pkg.emoji}</span>
                <div style={styles.packageInfo}>
                  <h3 style={styles.packageTitle}>{pkg.title}</h3>
                  <p style={styles.packageDetails}>⏱ {pkg.duration}</p>
                  <p style={styles.packageDetails}>🖼 {pkg.photos}</p>
                </div>
                <div style={styles.packagePriceWrap}>
                  <span style={styles.packagePrice}>${pkg.price}</span>
                  {selectedPackage === pkg.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      style={styles.checkMark}
                    >
                      ✓
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Location */}
        <p style={styles.sectionLabel}>SHOOT LOCATION</p>
        <div style={styles.locationGrid}>
          {locations.map((loc, i) => (
            <motion.div
              key={loc}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedLocation(loc)}
              style={{
                ...styles.locationChip,
                background: selectedLocation === loc ? '#A78BFA22' : '#111',
                border: selectedLocation === loc
                  ? '1px solid #A78BFA66'
                  : '1px solid #ffffff08',
                color: selectedLocation === loc ? '#A78BFA' : '#666',
              }}
            >
              {loc}
            </motion.div>
          ))}
        </div>

        {/* Date & Time */}
        <p style={styles.sectionLabel}>DATE & TIME</p>
        <div style={styles.dateTimeRow}>
          <div style={styles.inputWrap}>
            <span style={styles.inputIcon}>📅</span>
            <input
              type="date"
              style={styles.input}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div style={styles.inputWrap}>
            <span style={styles.inputIcon}>🕐</span>
            <input
              type="time"
              style={styles.input}
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        </div>

        {/* Submit */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          style={{
            ...styles.submitBtn,
            opacity: isReady ? 1 : 0.4,
            cursor: isReady ? 'pointer' : 'not-allowed',
          }}
          onClick={handleSubmit}
        >
          Book Photographer →
        </motion.button>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>POWERED BY <span style={styles.redspeed}>REDSPEED</span></p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#080808',
    maxWidth: '430px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    position: 'relative',
    overflow: 'hidden',
    paddingBottom: '40px',
  },
  orb1: {
    position: 'absolute', top: '-50px', right: '-100px',
    width: '350px', height: '350px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(167,139,250,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  orb2: {
    position: 'absolute', bottom: '20%', left: '-100px',
    width: '300px', height: '300px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(167,139,250,0.04) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  header: {
    padding: '24px 20px 20px',
    position: 'relative', zIndex: 1,
    textAlign: 'center',
  },
  backArrow: {
    position: 'absolute', left: '20px', top: '24px',
    background: '#ffffff0a', border: '1px solid #ffffff12',
    borderRadius: '20px', padding: '7px 14px',
    color: '#888', fontSize: '13px', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  headerCenter: { paddingTop: '8px' },
  headerEmoji: { fontSize: '36px', display: 'block', marginBottom: '8px' },
  headerTitle: {
    fontSize: '28px', fontWeight: '700',
    color: '#fff', margin: '0 0 4px', letterSpacing: '-0.5px',
  },
  headerSub: { fontSize: '13px', color: '#555', margin: 0 },
  roomBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: '#ffffff0a', border: '1px solid #ffffff12',
    borderRadius: '30px', padding: '6px 12px', marginTop: '12px',
  },
  roomDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    background: '#A78BFA', boxShadow: '0 0 6px #A78BFA',
    display: 'inline-block',
  },
  roomText: { fontSize: '12px', fontWeight: '600', color: '#ccc' },
  goldLine: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, #A78BFA44, #A78BFA, #A78BFA44, transparent)',
    margin: '4px 24px 20px',
    transformOrigin: 'left',
  },
  content: {
    padding: '0 16px',
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  sectionLabel: {
    fontSize: '10px', letterSpacing: '2px',
    color: '#444', fontWeight: '600', margin: '8px 0 4px',
  },
  packages: { display: 'flex', flexDirection: 'column', gap: '10px' },
  packageCard: {
    borderRadius: '16px', padding: '16px',
    cursor: 'pointer', position: 'relative',
    transition: 'all 0.2s ease',
  },
  popularBadge: {
    position: 'absolute', top: '-1px', right: '16px',
    background: '#A78BFA', color: '#000',
    fontSize: '9px', fontWeight: '700',
    letterSpacing: '1px', padding: '4px 10px',
    borderRadius: '0 0 8px 8px',
  },
  packageTop: {
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  packageEmoji: { fontSize: '28px', flexShrink: 0 },
  packageInfo: { flex: 1 },
  packageTitle: {
    fontSize: '15px', fontWeight: '700',
    color: '#fff', margin: '0 0 4px',
  },
  packageDetails: { fontSize: '12px', color: '#555', margin: '2px 0' },
  packagePriceWrap: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: '6px',
  },
  packagePrice: {
    fontSize: '20px', fontWeight: '700', color: '#A78BFA',
  },
  checkMark: {
    width: '20px', height: '20px', borderRadius: '50%',
    background: '#A78BFA', color: '#000',
    fontSize: '12px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  locationGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
  },
  locationChip: {
    borderRadius: '20px', padding: '8px 16px',
    fontSize: '13px', fontWeight: '500',
    cursor: 'pointer', transition: 'all 0.2s ease',
  },
  dateTimeRow: { display: 'flex', gap: '10px' },
  inputWrap: {
    flex: 1, background: '#111',
    border: '1px solid #ffffff10',
    borderRadius: '14px', padding: '4px 12px',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  inputIcon: { fontSize: '16px', flexShrink: 0 },
  input: {
    background: 'transparent', border: 'none', outline: 'none',
    color: '#fff', fontSize: '14px', width: '100%',
    padding: '10px 0', fontFamily: 'inherit',
    colorScheme: 'dark',
  },
  submitBtn: {
    width: '100%', background: '#A78BFA',
    color: '#000', border: 'none',
    borderRadius: '14px', padding: '16px',
    fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit',
    marginTop: '8px',
  },
  footer: { textAlign: 'center', padding: '32px 0 0' },
  footerText: { fontSize: '9px', letterSpacing: '3px', color: '#333' },
  redspeed: { color: '#FFFFFF' },
  successPage: {
    minHeight: '100vh', display: 'flex',
    flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '40px 24px',
    textAlign: 'center', position: 'relative', zIndex: 1,
  },
  successIconWrap: {
    width: '80px', height: '80px', borderRadius: '24px',
    background: '#A78BFA15', border: '1px solid #A78BFA30',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', marginBottom: '20px',
  },
  successIcon: { fontSize: '40px' },
  successTitle: {
    fontSize: '30px', fontWeight: '700',
    color: '#fff', margin: '0 0 8px',
  },
  successSub: { fontSize: '14px', color: '#555', marginBottom: '28px' },
  successCard: {
    background: '#111', border: '1px solid #ffffff10',
    borderRadius: '20px', padding: '20px',
    width: '100%', maxWidth: '320px', marginBottom: '24px',
  },
  successRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '8px 0',
  },
  successLabel: { fontSize: '10px', letterSpacing: '2px', color: '#555' },
  successValue: { fontSize: '14px', fontWeight: '600', color: '#fff' },
  successDivider: { height: '1px', background: '#ffffff08' },
  successNote: {
    fontSize: '13px', color: '#555',
    lineHeight: 1.6, marginBottom: '28px',
    maxWidth: '280px',
  },
  backBtn: {
    background: '#ffffff0a', border: '1px solid #ffffff15',
    borderRadius: '14px', padding: '14px 32px',
    color: '#fff', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default Photography;